"use client";

import React from 'react';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { X } from '@/components/icons';
import { cn } from '@/lib/utils';
import useHeaderOffset from '@/hooks/useHeaderOffset';

interface MinimizedProductsStackProps {
  products: Array<{ id: string; title: string }>;
  onProductClick: (productId: string) => void;
  onProductClose: (productId: string) => void;
}

export const MinimizedProductsStack: React.FC<MinimizedProductsStackProps> = ({
  products,
  onProductClick,
  onProductClose,
}) => {
  const { minimizedProducts, minimizedNotes } = useContentSidebar();
  const { bottomNavHeight, isDesktop } = useHeaderOffset();

  const minimizedProductIds = Object.keys(minimizedProducts).filter((id) => minimizedProducts[id]);
  
  // Deduplicate products by id - prevent rendering same product twice
  const uniqueProductsMap = new Map<string, { id: string; title: string }>();
  products.forEach((p) => {
    if (!uniqueProductsMap.has(p.id)) {
      uniqueProductsMap.set(p.id, p);
    }
  });
  const uniqueProducts = Array.from(uniqueProductsMap.values());
  
  const minimizedProductsList = uniqueProducts.filter((p) => minimizedProductIds.includes(p.id));

  if (minimizedProductsList.length === 0) return null;

  return (
    <>
      {minimizedProductsList.map((product, index) => {
        // Count how many notes are minimized to stack products on top
        const minimizedNoteCount = Object.values(minimizedNotes).filter(Boolean).length;
        const bottomOffset = !isDesktop ? bottomNavHeight + minimizedNoteCount * 64 + index * 64 : 0;
        const rightOffset = isDesktop ? index * 328 : 0; // 320px width + 8px gap
        const dynamicZIndex = !isDesktop ? 9910 + index : 9910; // Only increment z-index on mobile (vertical stack)

        return (
          <div
            key={product.id}
            className={cn(
              'fixed bottom-0 w-80 bg-background border rounded-t-lg shadow-lg',
              'transition-all duration-300',
              'max-md:w-full max-md:rounded-none'
            )}
            style={{
              right: isDesktop ? `${rightOffset}px` : 'auto',
              left: isDesktop ? 'auto' : '0',
              bottom: isDesktop ? '0' : `${bottomOffset}px`,
              zIndex: dynamicZIndex,
            }}
          >
            <div
              role="button"
              onClick={() => {
                onProductClick(product.id);
              }}
              className="w-full p-3 flex items-center justify-between transition-colors rounded-t-lg max-md:rounded-none hover:bg-muted/50 cursor-pointer"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-full bg-muted/50 flex-shrink-0" />
                <span className="font-medium truncate text-sm">{product.title}</span>
              </div>
              <div
                role="button"
                className="h-6 w-6 hover:bg-primary/20 rounded flex items-center justify-center transition-colors flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onProductClose(product.id);
                }}
              >
                <X className="w-4 h-4" />
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};
